/**
 * Configurator — pure data + math helpers.
 *
 * No React or DOM imports. Safe to import anywhere (atoms, hooks, tests).
 * `category`/`slug` strings MUST match `frontend/public/pricelist/prices.json`.
 */

export const TYPES = [
  { category: 'ramen',          slug: 'spannrahmen',     icon: 'window-frame' },
  { category: 'ramen',          slug: 'drehfenster',     icon: 'window-hinge' },
  { category: 'ramen',          slug: 'plissee-fenster', icon: 'plisse' },
  { category: 'ramen',          slug: 'rollo-fenster',   icon: 'rollo' },
  { category: 'ramen',          slug: 'dachfenster',     icon: 'window-roof' },
  { category: 'deuren',         slug: 'drehtuer',        icon: 'door-hinge' },
  { category: 'deuren',         slug: 'pendeltuer',      icon: 'door-swing' },
  { category: 'deuren',         slug: 'schiebetuer',     icon: 'door-slide' },
  { category: 'deuren',         slug: 'plisseetuer',     icon: 'door-plisse' },
  { category: 'lichtschachten', slug: 'standaard',       icon: 'light-shaft' },
];

export const COLORS = [
  { id: 'white',      hex: '#F4F1EA', up: 0 },
  { id: 'black',      hex: '#1F1D1A', up: 10 },
  { id: 'anthracite', hex: '#3D3A36', up: 15 },
  { id: 'brown',      hex: '#6B4A2B', up: 15 },
];

export const MESHES = [
  { id: 'standard', up: 0 },
  { id: 'pollen',   up: 35 },
  { id: 'pet',      up: 25 },
];

// Slug-specific UI gates — door variants need direction + cross-bar inputs.
export const NEEDS_OPENING_DIR = new Set(['drehtuer', 'pendeltuer']);
export const NEEDS_SLIDE_DIR   = new Set(['schiebetuer', 'plisseetuer']);
export const NEEDS_CROSSBAR    = new Set(['drehtuer', 'pendeltuer']);

// Long display names used in cart rows + summary aside.
const PRODUCT_NAMES = {
  de: {
    'spannrahmen':     'Spannrahmen',
    'drehfenster':     'Drehfenster-Insektenschutz',
    'plissee-fenster': 'Plissee-Insektenschutz (Fenster)',
    'rollo-fenster':   'Rollo-Insektenschutz (Fenster)',
    'dachfenster':     'Dachfenster-Insektenschutz',
    'drehtuer':        'Drehtür',
    'pendeltuer':      'Pendeltür',
    'schiebetuer':     'Schiebetür',
    'plisseetuer':     'Plissee-Tür',
    'standaard':       'Lichtschacht-Abdeckung',
  },
  nl: {
    'spannrahmen':     'Spanraamhor',
    'drehfenster':     'Draairaam-hor',
    'plissee-fenster': 'Plissé-hor (raam)',
    'rollo-fenster':   'Rolhor (raam)',
    'dachfenster':     'Dakraam-hor',
    'drehtuer':        'Draaihor',
    'pendeltuer':      'Pendelhor',
    'schiebetuer':     'Schuifhor',
    'plisseetuer':     'Plissé-deurhor',
    'standaard':       'Lichtschacht-afdekking',
  },
};

// Short labels used in the type-grid cards (step 1).
const SHORT_NAMES = {
  de: {
    'spannrahmen':     'Spannrahmen',
    'drehfenster':     'Drehfenster',
    'plissee-fenster': 'Plissee-Fenster',
    'rollo-fenster':   'Rollo-Fenster',
    'dachfenster':     'Dachfenster',
    'drehtuer':        'Drehtür',
    'pendeltuer':      'Pendeltür',
    'schiebetuer':     'Schiebetür',
    'plisseetuer':     'Plissee-Tür',
    'standaard':       'Lichtschacht',
  },
  nl: {
    'spannrahmen':     'Spanraamhor',
    'drehfenster':     'Draairaam',
    'plissee-fenster': 'Plissé-raam',
    'rollo-fenster':   'Rolhor-raam',
    'dachfenster':     'Dakraam',
    'drehtuer':        'Draaihor',
    'pendeltuer':      'Pendelhor',
    'schiebetuer':     'Schuifhor',
    'plisseetuer':     'Plissé-deur',
    'standaard':       'Lichtschacht',
  },
};

export function productNameFor(slug, lang) {
  return PRODUCT_NAMES[lang]?.[slug] || PRODUCT_NAMES.nl[slug] || slug;
}

export function shortNameFor(slug, lang) {
  return SHORT_NAMES[lang]?.[slug] || SHORT_NAMES.nl[slug] || slug;
}

function clamp(n, min, max) { return Math.max(min, Math.min(max, n)); }

/**
 * Compute the live estimate for a configured product.
 * Reference area is 100×60 cm = 600.000 mm². Below it we apply a soft
 * floor (0.85×) so tiny rollos still cost a sensible minimum; above we
 * cap growth at 2.5× to keep big-format quotes realistic.
 */
export function computePrice({ basePrice, width, height, colorUp, meshUp }) {
  if (!basePrice || !width || !height) return null;
  const area = width * height;
  const ratio = area / 600000;
  const areaFactor = clamp(0.85 + (ratio - 1) * 0.5, 0.85, 2.5);
  return Math.round(basePrice * areaFactor + colorUp + meshUp);
}

// Resolve the effective base price (sale-adjusted) from prices.json.
export function basePriceFromCatalog(prices, category, slug) {
  const raw = prices?.prices?.[category]?.[slug];
  if (raw == null) return null;
  if (typeof raw === 'number') return raw;
  if (typeof raw === 'object') {
    return typeof raw.sale === 'number' && raw.sale < raw.base ? raw.sale : raw.base;
  }
  return null;
}
